/**
 * RibbonButton — small / large tool buttons and palette buttons (Faz 3.1).
 * Rendering variants of the OCS RibbonItem Tool / LargeTool / panel-button
 * model, with the composed tooltip and the keytip badge overlay.
 */

import { Box, Icon, Text, Tooltip } from '@chakra-ui/react';
import { useMemo } from 'react';
import type { RibbonEntryDef, RibbonPanelButtonDef } from '../../../config/ribbonTabs';
import { useAppStore } from '../../../state/store';
import { runRibbonBinding, bindingTool } from './ribbonCommand';
import { KeytipBadge, useEntryTooltip, useItemLabel, panelButtonState } from './KeytipBadge';
import { usePanelMemoryStore } from './panelMemoryStore';

interface RibbonButtonProps {
    entry: RibbonEntryDef & { kind?: 'tool' | 'largeTool' };
    groupKey: string;
    large?: boolean;
    /** Show the keytip badge (items level of the Alt chain). */
    keytipsOn: boolean;
}

export function RibbonButton({ entry, groupKey, large = false, keytipsOn }: RibbonButtonProps) {
    const label = useItemLabel(entry.key);
    const tooltip = useEntryTooltip(entry);
    const IconCmp = entry.icon;
    const record = usePanelMemoryStore((s) => s.record);

    const activeTool = useAppStore((s) => s.activeTool);
    const tool = useMemo(() => bindingTool(entry), [entry]);
    const isActive = !!tool && activeTool === tool;

    const handleClick = () => {
        runRibbonBinding(entry);
        record(groupKey, entry.key);
    };

    if (!IconCmp) return null;

    if (large) {
        return (
            <Tooltip label={tooltip} placement="bottom" openDelay={400} hasArrow>
                <Box
                    as="button"
                    role="button"
                    tabIndex={0}
                    aria-label={label}
                    onClick={handleClick}
                    position="relative"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap="2px"
                    w="72px"
                    h="full"
                    flexShrink={0}
                    px={1}
                    borderRadius="4px"
                    bg={isActive ? 'bg.active' : 'transparent'}
                    _hover={{ bg: isActive ? 'bg.active' : 'bg.hover' }}
                >
                    <Icon as={IconCmp} boxSize="22px" color="icon.base" />
                    <Text fontSize="10px" lineHeight="12px" color="text.primary" noOfLines={1} maxW="68px">
                        {label}
                    </Text>
                    <KeytipBadge letter={entry.keytip} show={keytipsOn} />
                </Box>
            </Tooltip>
        );
    }

    return (
        <Tooltip label={tooltip} placement="bottom" openDelay={400} hasArrow>
            <Box
                as="button"
                role="button"
                tabIndex={0}
                aria-label={label}
                onClick={handleClick}
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="26px"
                h="26px"
                flexShrink={0}
                borderRadius="4px"
                bg={isActive ? 'bg.active' : 'transparent'}
                _hover={{ bg: isActive ? 'bg.active' : 'bg.hover' }}
            >
                <Icon as={IconCmp} boxSize="15px" color="icon.base" />
                <KeytipBadge letter={entry.keytip} show={keytipsOn} />
            </Box>
        </Tooltip>
    );
}

/** Palette opener button (panelButton items). */
export function RibbonPanelButton({ entry, groupKey, large = false, keytipsOn }: {
    entry: RibbonPanelButtonDef;
    groupKey: string;
    large?: boolean;
    keytipsOn: boolean;
}) {
    const label = useItemLabel(entry.key);
    const tooltip = useEntryTooltip(entry);
    const IconCmp = entry.icon;
    const { available, run } = panelButtonState(entry.panelId);
    const record = usePanelMemoryStore((s) => s.record);

    if (!IconCmp) return null;

    const handleClick = () => {
        if (!available) return;
        run();
        record(groupKey, entry.key);
    };

    return (
        <Tooltip
            label={available ? tooltip : `${tooltip}\n(available with the Faz 5 palettes)`}
            placement="bottom"
            openDelay={400}
            hasArrow
        >
            <Box
                as="button"
                role="button"
                tabIndex={0}
                aria-label={label}
                onClick={handleClick}
                position="relative"
                display="flex"
                flexDirection={large ? 'column' : 'row'}
                alignItems="center"
                justifyContent="center"
                gap={large ? '2px' : undefined}
                w={large ? '72px' : '26px'}
                h={large ? 'full' : '26px'}
                flexShrink={0}
                px={1}
                borderRadius="4px"
                bg="transparent"
                opacity={available ? 1 : 0.45}
                cursor={available ? 'pointer' : 'not-allowed'}
                _hover={available ? { bg: 'bg.hover' } : undefined}
            >
                <Icon as={IconCmp} boxSize={large ? '22px' : '15px'} color="icon.base" />
                {large && (
                    <Text fontSize="10px" lineHeight="12px" color="text.primary" noOfLines={1} maxW="68px">
                        {label}
                    </Text>
                )}
                <KeytipBadge letter={entry.keytip} show={keytipsOn} />
            </Box>
        </Tooltip>
    );
}
